import { CSSProperties, FC, ReactNode, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  Skeleton,
} from "@mui/material";
import { makeSxStyles } from "ui";
import { SxProps } from "@mui/system";
import { Nullable } from "service";
import { useSearchParams } from "react-router-dom";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import GenericTableRows from "./GenericTableRows";
import DraggableTable from "./draggable-table";

export interface LabelValueItem {
  label: string | ReactNode;
  value: string;
  onCellClick?: (value: string) => void;
  size?: string;
  minSize?: string;
  sortable?: boolean;
  sortableValue?: string;
  customCSS?: CSSProperties;
  minWidth?: string;
  index?: any;
}

type SortDirection = "asc" | "desc";

export interface SortOptions {
  sortBy: string; // Normally the value of the header
  direction: SortDirection;
}

export interface GenericTableRow {
  values: LabelValueItem[];
  item: any;
}

interface GenericTableHoverAction {
  node: ReactNode;
}

export type RenderGenericRowFunction<T = unknown> = (props: {
  header: LabelValueItem;
  data: T;
  headerValue: string;
  index: any;
  setActiveRow: React.Dispatch<React.SetStateAction<number | null>>;
}) => LabelValueItem;

export interface GenericTableProps {
  headers: LabelValueItem[];
  secondHeaders?: LabelValueItem[];
  renderRowLabel: Record<string, RenderGenericRowFunction<any>>;
  onRowClick?: (data: any, e: React.MouseEvent) => void;
  data: any[];
  onHoverActions?: GenericTableHoverAction[];
  sx?: SxProps<Theme>;
  sortOptions?: Nullable<SortOptions>;
  onSortChange?: (options: Nullable<SortOptions>) => void;
  isLoading?: boolean;
  isHeaderSticky?: boolean;
  draggable?: boolean;
  shouldSetActiveRow?: boolean;
  shouldAddOnHover?: boolean;
  onAddClick?: (index: number) => void;
  defaultActiveRow?: number | null;
  tableWidth?: string; // Dynamic table width for zoom functionality
  compactCellPadding?: boolean; // 6px on all sides (route planner, region management). Default: 24px left/right
}

const GenericTable: FC<GenericTableProps> = (props) => {
  const [_, setSearchParams] = useSearchParams();
  const styles = useStyles();
  const [activeRow, setActiveRow] = useState<number | null>(
    props.defaultActiveRow === undefined ? null : props.defaultActiveRow,
  );
  const {
    headers,
    secondHeaders,
    renderRowLabel,
    onRowClick,
    data,
    sx,
    sortOptions,
    onSortChange,
    isLoading = false,
    isHeaderSticky = false,
    draggable = false,
    shouldSetActiveRow = true,
    shouldAddOnHover,
    onAddClick,
    tableWidth,
    compactCellPadding = false,
  } = props;

  const renderedRows = useMemo(() => {
    const result: GenericTableRow[] = [];
    let i = 0;
    for (const item of data) {
      const labels: LabelValueItem[] = [];
      i++;
      for (const header of headers) {
        const label = renderRowLabel[header.value]?.({
          header,
          data: item,
          headerValue: header.value,
          index: i,
          setActiveRow,
        });
        if (!label) {
          continue;
        }
        labels.push(label);
      }
      result.push({
        values: labels,
        item,
      });
    }
    return result;
  }, [headers, data, renderRowLabel]);

  const onSort = (value: string) => {
    if (!onSortChange) {
      return;
    }

    // Click on sort option and none is set currently
    if (!sortOptions || sortOptions.sortBy !== value) {
      onSortChange({
        sortBy: value,
        direction: "asc",
      });
      setSearchParams((params) => {
        params.set("sortBy", value);
        params.set("direction", "asc");
        return params;
      });
      return;
    }

    // Promote the already matching sort option to reversed ordering
    if (sortOptions.direction === "asc") {
      onSortChange({
        sortBy: value,
        direction: "desc",
      });
      setSearchParams((params) => {
        params.set("sortBy", value);
        params.set("direction", "desc");
        return params;
      });
      return;
    }

    // If already promoted unset the sorting entirelly
    onSortChange(null);
    setSearchParams((params) => {
      params.delete("sortBy");
      params.delete("direction");
      return params;
    });
  };

  const TableRowsLoader = ({
    rowsNum,
    cellNum,
  }: {
    rowsNum: number;
    cellNum: number;
  }) => {
    return (
      <>
        {[...Array(rowsNum)].map((_row, index) => (
          <TableRow sx={styles.tableRow} key={index}>
            <TableCell component="th" scope="row">
              <Skeleton animation="wave" variant="text" />
            </TableCell>
            {[...Array(cellNum - 1)].map((_cell, indexT) => (
              <TableCell key={indexT}>
                <Skeleton animation="wave" variant="text" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <TableContainer component={Paper} sx={sx} data-testid="generic-table">
      <Table
        sx={{
          ...styles.table,
          ...(tableWidth && {
            width: tableWidth,
            minWidth: tableWidth,
          }),
          ...(compactCellPadding && {
            "& .MuiTableCell-root": {
              padding: "6px",
            },
          }),
        }}
        stickyHeader={isHeaderSticky}
      >
        <TableHead
          sx={{
            ...(secondHeaders ? styles.tableHeadCompact : styles.tableHead),
            minHeight: secondHeaders ? "auto" : undefined,
            ...(tableWidth && {
              width: tableWidth,
              minWidth: tableWidth,
            }),
          }}
        >
          <TableRow
            sx={{
              height: secondHeaders ? "auto" : undefined,
              ...(tableWidth && {
                width: tableWidth,
                minWidth: tableWidth,
              }),
            }}
          >
            {headers.map((h) => {
              const sortVal = h.sortableValue ? h.sortableValue : h.value;
              return (
                <TableCell
                  key={h.value}
                  data-header-value={h.value}
                  sx={{
                    ...(secondHeaders
                      ? styles.tableHeadCellCompact
                      : styles.tableHeadCell),
                    width: h.size,
                    ...(h.minWidth ? { minWidth: h.minWidth } : {}),
                  }}
                  style={h.customCSS}
                  onClick={h.sortable ? () => onSort(sortVal) : undefined}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={
                      h.value === "notifications" ? "center" : "left"
                    }
                    sx={{ lineHeight: "1.2", height: "auto" }}
                  >
                    {typeof h.label === "string" ? (
                      <Typography variant="caption" sx={{ lineHeight: "1.2" }}>
                        {h.label}
                      </Typography>
                    ) : (
                      h.label
                    )}
                    {h.sortable && (
                      <Box
                        display="flex"
                        flexDirection="column"
                        sx={{
                          opacity: sortOptions?.sortBy === sortVal ? 1 : 0.5,
                        }}
                      >
                        <ArrowDropUp
                          sx={
                            sortOptions?.direction === "desc" &&
                            sortOptions?.sortBy === sortVal
                              ? { ...styles.icons, opacity: 0.5 }
                              : styles.icons
                          }
                        />
                        <ArrowDropDown
                          sx={
                            sortOptions?.direction === "asc" &&
                            sortOptions?.sortBy === sortVal
                              ? { ...styles.icons, opacity: 0.5 }
                              : styles.icons
                          }
                        />
                      </Box>
                    )}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
          {secondHeaders && (
            <TableRow
              sx={{
                height: "auto",
                ...(tableWidth && {
                  width: tableWidth,
                  minWidth: tableWidth,
                }),
                "& .MuiTableCell-root": {
                  backgroundColor: "#282A2D !important",
                  paddingTop: "6px !important",
                  paddingBottom: "6px !important",
                  paddingLeft: "24px",
                  paddingRight: "24px",
                  lineHeight: "1.2",
                  "& .MuiTypography-root": {
                    lineHeight: "1.2",
                    fontSize: "12px",
                  },
                },
                "& .MuiTableCell-root[data-header-value='skipped']": {
                  backgroundColor: "#1E2022 !important",
                },
                "& .MuiTableCell-root[data-header-value='actions']": {
                  backgroundColor: "#1E2022 !important",
                },
              }}
            >
              {secondHeaders.map((h) => {
                const sortVal = h.sortableValue ? h.sortableValue : h.value;
                return (
                  <TableCell
                    key={h.value}
                    data-header-value={h.value}
                    sx={{
                      ...(secondHeaders
                        ? styles.tableHeadCellCompact
                        : styles.tableHeadCell),
                      width: h.size,
                      ...(h.minWidth ? { minWidth: h.minWidth } : {}),
                    }}
                    style={h.customCSS}
                    onClick={h.sortable ? () => onSort(sortVal) : undefined}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="left"
                      sx={{ lineHeight: "1.2", height: "auto" }}
                    >
                      {typeof h.label === "string" ? (
                        <Typography
                          variant="caption"
                          sx={{ lineHeight: "1.2" }}
                        >
                          {h.label}
                        </Typography>
                      ) : (
                        h.label
                      )}
                      {h.sortable && (
                        <Box
                          display="flex"
                          flexDirection="column"
                          sx={{
                            opacity: sortOptions?.sortBy === sortVal ? 1 : 0.5,
                          }}
                        >
                          <ArrowDropUp
                            sx={
                              sortOptions?.direction === "desc" &&
                              sortOptions?.sortBy === sortVal
                                ? { ...styles.icons, opacity: 0.5 }
                                : styles.icons
                            }
                          />
                          <ArrowDropDown
                            sx={
                              sortOptions?.direction === "asc" &&
                              sortOptions?.sortBy === sortVal
                                ? { ...styles.icons, opacity: 0.5 }
                                : styles.icons
                            }
                          />
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {isLoading || !data ? (
            <TableRowsLoader rowsNum={10} cellNum={headers.length} />
          ) : draggable ? (
            <DraggableTable
              renderedRows={renderedRows}
              activeRow={activeRow}
              shouldAddOnHover={shouldAddOnHover}
              onAddClick={onAddClick}
              tableWidth={tableWidth}
            />
          ) : (
            <GenericTableRows
              renderedRows={renderedRows}
              headers={headers}
              onRowClick={onRowClick}
              activeRow={activeRow}
              setActiveRow={shouldSetActiveRow ? setActiveRow : undefined}
              draggable={false}
              tableWidth={tableWidth}
            />
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const useStyles = makeSxStyles(({ palette: { grey } }) => ({
  tableHead: {
    height: "70px",
  },
  tableHeadCompact: {
    height: "auto",
  },
  tableHeadCell: {
    fontWeight: 500,
    background: grey["500"],
    paddingLeft: "24px",
    paddingRight: "24px",
  },
  tableHeadCellCompact: {
    fontWeight: 500,
    background: grey["500"],
    paddingTop: "6px !important",
    paddingBottom: "6px !important",
    paddingLeft: "24px",
    paddingRight: "24px",
    lineHeight: "1.2",
    "& .MuiTypography-root": {
      lineHeight: "1.2",
      fontSize: "12px",
    },
  },
  tableColumnCell: {
    fontWeight: 400,
  },
  tableRow: {
    height: "70px",
    background: grey["800"],
    ".toShow": {
      opacity: 0,
      visibility: "hidden",
      display: "flex",
    },
    "&:hover": {
      ".toHide": {
        display: "none",
      },
      ".toShow": {
        opacity: 1,
        visibility: "visible",
        display: "flex",
      },
    },
    "&:visited": {
      outline: "1px solid #fff",
    },
  },
  activeRow: {
    outline: "1px solid #fff",
  },
  tableRowClickable: {
    "&:hover": {
      cursor: "pointer",
    },
  },
  table: {
    width: "100%",
  },
  container: {
    padding: "10px 20px 30px",
  },
  loader: {
    margin: "0 auto",
  },
  icons: {
    margin: "-4px 0",
    color: grey["100"],
    fontSize: "16px",
  },
}));

export default GenericTable;
